import { forwardRef, useId, useState } from 'react'
import type { ChangeEvent, TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'
import { FieldFrame, describedBy, fieldControlClass } from '@/components/ui/Input'

const nf = new Intl.NumberFormat('pt-BR')

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string
  hint?: string
  error?: string
  id?: string
  /** Exibe o contador "1.234 / 2.200". */
  showCount?: boolean
  /**
   * Quando `true`, o navegador impede digitar além de `maxLength`.
   * Por padrão o limite é apenas visual, para o usuário ver o quanto excedeu.
   */
  hardLimit?: boolean
  containerClassName?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    hint,
    error,
    required,
    id,
    showCount = false,
    hardLimit = false,
    maxLength,
    value,
    defaultValue,
    onChange,
    containerClassName,
    className,
    rows = 4,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  ref,
) {
  const generatedId = useId()
  const controlId = id ?? `textarea-${generatedId}`
  const hintId = `${controlId}-hint`
  const errorId = `${controlId}-error`
  const countId = `${controlId}-count`

  const [uncontrolledLength, setUncontrolledLength] = useState(
    () => String(defaultValue ?? '').length,
  )
  const length = value !== undefined ? String(value).length : uncontrolledLength

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    if (value === undefined) setUncontrolledLength(event.target.value.length)
    onChange?.(event)
  }

  const ratio = maxLength ? length / maxLength : 0
  const over = Boolean(maxLength) && length > (maxLength ?? 0)
  const near = !over && ratio >= 0.9

  const counter =
    showCount && maxLength ? (
      <span
        id={countId}
        className={cn(
          'text-xs font-medium tabular-nums',
          over
            ? 'text-[var(--danger)]'
            : near
              ? 'text-[var(--warning)]'
              : 'text-[var(--text-muted)]',
        )}
      >
        {nf.format(length)} / {nf.format(maxLength)}
        {over && <span className="sr-only"> caracteres — limite excedido</span>}
      </span>
    ) : undefined

  return (
    <FieldFrame
      controlId={controlId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      hintId={hintId}
      errorId={errorId}
      meta={counter}
      className={containerClassName}
    >
      <textarea
        ref={ref}
        id={controlId}
        rows={rows}
        required={required}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        maxLength={hardLimit ? maxLength : undefined}
        aria-invalid={error || over ? true : undefined}
        aria-describedby={describedBy(
          hint && !error && hintId,
          error && errorId,
          counter && countId,
          ariaDescribedBy,
        )}
        className={cn(
          fieldControlClass(Boolean(error) || over),
          'sh-scroll-thin min-h-24 resize-y leading-6',
          className,
        )}
        {...props}
      />
    </FieldFrame>
  )
})
